import { useState } from "react";
import { X, Sparkle, ArrowRight, Microphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../../lib/teleproApi";
import { STATUS_BY_ID } from "./callListConstants";
import { useVoiceDictation } from "./useVoiceDictation";

/**
 * PostCallAIModal — Vague 11-A.
 *
 * Full-screen (mobile) / centered (desktop) modal that opens when the télépro
 * taps « 🤖 Aide-moi après l'appel ». They type or dictate a few keywords,
 * click Analyser, and the IA returns a clean note + suggested status +
 * callback_at + next_action. One click to apply, everything gets filled in.
 *
 * Props :
 *   open        : bool
 *   onClose()   : dismiss
 *   biz         : { id, name, city, category }
 *   onApply(patch) : called when user clicks « Tout appliquer », receives
 *                    {status, note, callback_at?, next_action}
 */
export default function PostCallAIModal({ open, onClose, biz, onApply }) {
  const [raw, setRaw] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { isSupported: micOk, isListening, interim, start, stop } = useVoiceDictation({
    lang: "fr-FR",
    onFinal: (chunk) => setRaw((cur) => (cur ? cur + " " : "") + chunk),
  });

  if (!open) return null;

  const analyse = async () => {
    if (!raw.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await teleproApi.post("/ai/post-call", {
        business_id: biz.id, raw_notes: raw.trim(),
      });
      if (r?.data && !r.data.error) setResult(r.data);
      else toast.error("L'IA n'a rien pu déduire — édite manuellement");
    } catch (e) {
      toast.error(`Erreur IA : ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const applyAll = () => {
    if (!result) return;
    const patch = {
      note: result.clean_note || raw,
    };
    if (result.status) patch.status = result.status;
    if (result.callback_at) patch.callback_at = result.callback_at;
    if (result.callback_reason) patch.callback_reason = result.callback_reason;
    onApply?.(patch);
    toast.success("Fiche mise à jour par l'IA ✨");
    setRaw("");
    setResult(null);
    onClose?.();
  };

  const statusMeta = result?.status ? STATUS_BY_ID[result.status] : null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}/>
      <div
        className="fixed inset-x-3 top-6 bottom-6 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[540px] sm:max-w-[92vw] sm:max-h-[85vh] z-50 bg-card border border-primary/30 rounded-sm shadow-xl overflow-y-auto"
        data-testid="post-call-ai-modal"
      >
        <div className="sticky top-0 bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkle size={18} weight="fill" className="text-yellow-300 shrink-0"/>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider opacity-80">Assistant IA post-appel</div>
              <div className="font-display font-medium text-sm truncate">{biz.name}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-primary-foreground/80 hover:text-white p-1"
            data-testid="post-call-ai-close"
          >
            <X size={16} weight="bold"/>
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 block">
              Tape ou dicte quelques mots-clés — même en désordre
            </label>
            <div className="flex items-start gap-1.5">
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value.slice(0, 500))}
                rows={3}
                autoFocus
                placeholder="Ex. « intéressé patron mardi 14h pas de site web »"
                className="flex-1 text-sm border border-border rounded-sm px-3 py-2 bg-background focus:border-primary outline-none"
                data-testid="post-call-ai-textarea"
              />
              {micOk && (
                <button
                  onClick={isListening ? stop : start}
                  className={`rounded-sm border transition-colors min-h-[36px] min-w-[36px] inline-flex items-center justify-center ${
                    isListening
                      ? "bg-secondary text-secondary-foreground border-secondary animate-pulse"
                      : "bg-muted/40 text-muted-foreground border-border hover:bg-primary/10 hover:text-primary"
                  }`}
                  data-testid="post-call-ai-mic"
                  title={isListening ? "Arrêter la dictée" : "Dicter"}
                >
                  <Microphone size={16}/>
                </button>
              )}
            </div>
            {isListening && interim && (
              <div className="text-[11px] italic text-secondary mt-1">{interim}…</div>
            )}
          </div>

          <button
            onClick={analyse}
            disabled={loading || !raw.trim()}
            className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] disabled:opacity-50 rounded-sm py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5"
            data-testid="post-call-ai-analyse"
          >
            {loading ? "Analyse en cours…" : <>
              <Sparkle size={14} weight="fill"/> Analyser avec l'IA
            </>}
          </button>

          {result && (
            <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 space-y-2.5" data-testid="post-call-ai-result">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">Statut suggéré</div>
                {statusMeta ? (
                  <div className="inline-flex items-center gap-1.5 bg-card border border-primary/40 rounded-sm px-2 py-1 text-xs font-bold" style={{ color: statusMeta.color }}>
                    {statusMeta.label}
                    {result.confidence > 0 && (
                      <span className="text-[9px] text-muted-foreground normal-case tracking-normal font-normal ml-1">
                        {Math.round(result.confidence * 100)}%
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">— aucun statut clair, choisis à la main</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">Note reformulée</div>
                <div className="text-sm bg-card border border-border rounded-sm px-2 py-1.5 leading-snug">
                  {result.clean_note || <em className="text-muted-foreground">(vide)</em>}
                </div>
              </div>
              {result.callback_at && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">Rappel programmé</div>
                  <div className="text-sm font-mono tabular-nums">
                    {new Date(result.callback_at).toLocaleString("fr-FR", {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                    {result.callback_reason && (
                      <span className="ml-2 text-xs text-muted-foreground italic">« {result.callback_reason} »</span>
                    )}
                  </div>
                </div>
              )}
              {result.next_action && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">Prochaine action</div>
                  <div className="text-sm text-secondary flex items-start gap-1">
                    <ArrowRight size={12} weight="bold" className="mt-1 shrink-0"/>
                    {result.next_action}
                  </div>
                </div>
              )}
              <button
                onClick={applyAll}
                className="w-full bg-secondary text-secondary-foreground hover:bg-[#a86647] rounded-sm py-2.5 text-sm font-bold mt-2"
                data-testid="post-call-ai-apply"
              >
                ✨ Tout appliquer sur la fiche
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
